import { useEffect, useRef, useState } from 'react'
import { importLibrary, setOptions } from '@googlemaps/js-api-loader'
import { MapPin } from 'lucide-react'

export type LocationSelection = {
  latitude: number
  longitude: number
  address?: string
  placeName?: string
}

type GoogleLocationPickerProps = {
  value?: LocationSelection | null
  initialCenter?: { latitude: number; longitude: number } | null
  onChange: (location: LocationSelection) => void
  searchPlaceholder?: string
  className?: string
  mapClassName?: string
}

const DEFAULT_CENTER = { latitude: 33.57311, longitude: -7.589843 }
const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined
const mapId = (import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string | undefined) || 'DEMO_MAP_ID'
let googleMapsPromise: Promise<typeof google> | null = null
let optionsConfigured = false

function loadGoogleMaps() {
  if (!apiKey) {
    return Promise.reject(new Error('Missing VITE_GOOGLE_MAPS_API_KEY.'))
  }

  if (!googleMapsPromise) {
    if (!optionsConfigured) {
      setOptions({ key: apiKey, v: 'weekly' })
      optionsConfigured = true
    }

    googleMapsPromise = importLibrary('maps').then(() => google)
  }

  return googleMapsPromise
}

function toLatLngLiteral(location: { latitude: number; longitude: number }): google.maps.LatLngLiteral {
  return { lat: location.latitude, lng: location.longitude }
}

function normalizeLatLng(latLng: google.maps.LatLng | google.maps.LatLngLiteral): LocationSelection {
  const lat = typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat
  const lng = typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng
  return {
    latitude: Number(lat.toFixed(6)),
    longitude: Number(lng.toFixed(6)),
  }
}

export function googleMapsUrl(location: Pick<LocationSelection, 'latitude' | 'longitude'>) {
  return `https://www.google.com/maps?q=${location.latitude.toFixed(6)},${location.longitude.toFixed(6)}`
}

export function GoogleLocationPicker({
  value,
  initialCenter,
  onChange,
  searchPlaceholder = 'Search for an address or place',
  className = '',
  mapClassName = 'h-80',
}: GoogleLocationPickerProps) {
  const mapElementRef = useRef<HTMLDivElement | null>(null)
  const autocompleteElementRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null)
  const geocoderRef = useRef<google.maps.Geocoder | null>(null)
  const autocompleteRef = useRef<HTMLElement | null>(null)
  const latestOnChange = useRef(onChange)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  latestOnChange.current = onChange

  useEffect(() => {
    let cancelled = false

    loadGoogleMaps()
      .then(async () => {
        if (cancelled || !mapElementRef.current || !autocompleteElementRef.current) return

        const mapElement = mapElementRef.current
        const autocompleteContainer = autocompleteElementRef.current
        const center = toLatLngLiteral(value ?? initialCenter ?? DEFAULT_CENTER)
        const map = new google.maps.Map(mapElement, {
          center,
          zoom: value ? 16 : 13,
          mapId,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
        })
        let marker: google.maps.marker.AdvancedMarkerElement | null = null
        try {
          await importLibrary('marker')
          if (cancelled) return
          marker = new google.maps.marker.AdvancedMarkerElement({
            map,
            position: center,
            gmpDraggable: true,
          })
        } catch {
          marker = null
        }

        const fallbackInput = document.createElement('input')
        fallbackInput.type = 'search'
        fallbackInput.disabled = true
        fallbackInput.placeholder = searchPlaceholder
        fallbackInput.className = 'h-9 min-w-0 w-full bg-transparent text-sm text-gray-400 focus:outline-none'
        autocompleteContainer.replaceChildren(fallbackInput)

        const commitLocation = (location: LocationSelection, reverseGeocode = false) => {
          if (marker) marker.position = toLatLngLiteral(location)
          map.panTo(toLatLngLiteral(location))

          if (!reverseGeocode || !geocoderRef.current) {
            latestOnChange.current(location)
            return
          }

          geocoderRef.current.geocode({ location: toLatLngLiteral(location) }, (results, status) => {
            if (status === 'OK' && results?.[0]?.formatted_address) {
              latestOnChange.current({
                ...location,
                address: results[0].formatted_address,
                placeName: results[0].formatted_address,
              })
              return
            }
            latestOnChange.current(location)
          })
        }

        map.addListener('click', (event: google.maps.MapMouseEvent) => {
          if (!event.latLng) return
          commitLocation(normalizeLatLng(event.latLng), true)
        })

        if (marker) {
          marker.addEventListener('gmp-dragend', () => {
            const position = marker?.position
            if (!position) return
            commitLocation(normalizeLatLng(position as google.maps.LatLng | google.maps.LatLngLiteral), true)
          })
        }

        void importLibrary('geocoding')
          .then(() => {
            if (!cancelled) geocoderRef.current = new google.maps.Geocoder()
          })
          .catch(() => undefined)

        void importLibrary('places')
          .then(() => {
            if (cancelled || !autocompleteElementRef.current) return
            const autocomplete = new google.maps.places.PlaceAutocompleteElement({})
            autocomplete.placeholder = searchPlaceholder
            autocomplete.className = 'block w-full'
            autocompleteElementRef.current.replaceChildren(autocomplete)
            autocomplete.addEventListener('gmp-select', async (event: Event) => {
              const selectEvent = event as Event & {
                place?: any
                placePrediction?: { toPlace: () => any }
              }
              const place = selectEvent.place ?? selectEvent.placePrediction?.toPlace()
              if (!place) return

              if (typeof place.fetchFields === 'function') {
                await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] })
              }

              const location = place.location ?? place.geometry?.location
              if (!location) return

              const selected = normalizeLatLng(location)
              const address = place.formattedAddress || place.formatted_address || place.displayName || place.name || undefined
              commitLocation({
                ...selected,
                address,
                placeName: place.displayName || place.name || address,
              })
              map.setZoom(16)
            })
            autocompleteRef.current = autocomplete
          })
          .catch(() => undefined)

        mapRef.current = map
        markerRef.current = marker
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setLoadError(error instanceof Error ? error.message : 'Could not load Google Maps.')
        setLoading(false)
      })

    return () => {
      cancelled = true
      autocompleteRef.current = null
      geocoderRef.current = null
      markerRef.current = null
      mapRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!value || !mapRef.current || !markerRef.current) return
    const position = toLatLngLiteral(value)
    markerRef.current.position = position
    mapRef.current.panTo(position)
  }, [value?.latitude, value?.longitude])

  return (
    <div className={className}>
      <div className="mb-3 flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm">
        <MapPin className="h-4 w-4 shrink-0 text-gray-500" />
        <div ref={autocompleteElementRef} className="min-w-0 flex-1" />
      </div>

      <div className={`relative overflow-hidden rounded-2xl border border-gray-200 bg-gray-100 ${mapClassName}`}>
        <div ref={mapElementRef} className="h-full w-full" />
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 text-sm text-gray-600">
            Loading map...
          </div>
        ) : null}
        {loadError ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white p-4 text-center text-sm text-red-600">
            {loadError}
          </div>
        ) : null}
      </div>

      <p className="mt-2 text-xs text-gray-500">
        Search for a place, click the map, or drag the pin to set the exact GPS location.
      </p>
    </div>
  )
}
